// Print-location rail for the Designer (the thumbnail column in the right panel).
// Renders ALL revealed locations as thumbnails — active gets a gold ring,
// others are muted but clickable to switch. Forwards switching, reveal/hide
// and remove back to the page via props. Canvas state lives in the page.
import type { Dispatch, SetStateAction, MutableRefObject } from 'react'
import type { CatProduct, CatColour } from '../../lib/catalog-types'
import { silhouettePath, CANVAS_W } from './helpers'

export interface DesignerLocationsProps {
  product: CatProduct
  colour: CatColour | undefined
  activeLocationId: string | null
  revealedLocationIds: Set<string>
  setRevealedLocationIds: Dispatch<SetStateAction<Set<string>>>
  locationThumbs: Record<string, string>
  setLocationThumbs: Dispatch<SetStateAction<Record<string, string>>>
  locationStatesRef: MutableRefObject<Record<string, object>>
  switchLocation: (nextId: string) => void
  apiSrc: (path?: string | null) => string
}

export default function DesignerLocations({
  product, colour, activeLocationId, revealedLocationIds, setRevealedLocationIds,
  locationThumbs, setLocationThumbs, locationStatesRef, switchLocation, apiSrc,
}: DesignerLocationsProps) {
  const all = product.printLocations ?? []
  const isFront = (loc: { name: string }) => loc.name.trim().toLowerCase() === 'front'
  const frontLoc = all.find(isFront)
  const revealed = all.filter((l) => revealedLocationIds.has(l.id))
  const hidden   = all.filter((l) => !revealedLocationIds.has(l.id))

  const thumbW = 80
  const thumbH = 90
  const scale  = thumbW / CANVAS_W

  return (
    <div className="p-3 space-y-3 overflow-y-auto flex-1">

      {/* All revealed locations — thumbnails */}
      <div className="space-y-2">
        {revealed.map((loc) => {
          const active = loc.id === activeLocationId
          const isLockedFront = frontLoc != null && loc.id === frontLoc.id
          const scMockup = (product.sideColourMockups ?? []).find((m) => m.locationId === loc.id && m.colourId === colour?.id)
          const bg = scMockup?.url ?? (colour?.mockupUrl ?? null) ?? (loc.mockupUrl ? apiSrc(loc.mockupUrl) : null) ?? (product.mockupUrl ? apiSrc(product.mockupUrl) : null)
          const r = { left: loc.left * scale, top: loc.top * scale, width: loc.width * scale, height: loc.height * scale }
          const thumb = locationThumbs[loc.id]

          return (
            <div key={loc.id} className="relative group/card">
              <button
                onClick={() => switchLocation(loc.id)}
                className="w-full focus:outline-none"
                title={active ? loc.name : `Switch to ${loc.name}`}>
                <div
                  className={[
                    'relative bg-surface rounded overflow-hidden mx-auto transition-all duration-150',
                    active
                      ? 'ring-2 ring-gold shadow-[0_2px_8px_rgba(0,0,0,0.12)]'
                      : 'ring-1 ring-border hover:ring-ink-3 hover:shadow-sm opacity-70 hover:opacity-100',
                  ].join(' ')}
                  style={{ width: thumbW, height: thumbH }}>
                  {bg ? (
                    <img src={bg} alt="" className="absolute inset-0 w-full h-full object-contain" draggable={false} />
                  ) : (
                    <svg viewBox="0 0 400 480" className="absolute inset-0 w-full h-full" style={{ color: colour?.hex ?? '#222' }}>
                      <path d={silhouettePath(product.slug)} fill="currentColor" />
                    </svg>
                  )}
                  <div className="absolute" style={{ ...r, background: thumb ? 'transparent' : 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' }}>
                    {thumb && <img src={thumb} alt="" draggable={false} className="absolute inset-0 w-full h-full object-contain" />}
                  </div>
                  {active && <div className="absolute bottom-0 inset-x-0 h-0.5 bg-gold" />}
                </div>
                <span className={'block text-center text-[10px] font-medium mt-1 leading-none ' + (active ? 'text-ink' : 'text-ink-3 group-hover/card:text-ink-2')}>
                  {loc.name}
                </span>
              </button>

              {/* Remove / hide button — top-right overlay on hover */}
              {!isLockedFront && revealed.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (active) {
                      if (!confirm(`Remove the ${loc.name} print location? Any artwork on it will be cleared and the location will move back to "+ Add print location".`)) return
                      delete locationStatesRef.current[loc.id]
                      setLocationThumbs((prev) => { const next = { ...prev }; delete next[loc.id]; return next })
                      setRevealedLocationIds((prev) => { const next = new Set(prev); next.delete(loc.id); return next })
                      const fallback = (frontLoc && frontLoc.id !== loc.id) ? frontLoc.id : revealed.find((l) => l.id !== loc.id)?.id
                      if (fallback) switchLocation(fallback)
                    } else {
                      setRevealedLocationIds((prev) => { const next = new Set(prev); next.delete(loc.id); return next })
                    }
                  }}
                  title={active ? `Remove ${loc.name} print location` : `Hide ${loc.name}`}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white border border-border text-ink-3 text-[11px] leading-none flex items-center justify-center shadow-sm opacity-0 group-hover/card:opacity-100 hover:bg-red-50 hover:text-red-500 hover:border-red-300 transition-all">
                  ×
                </button>
              )}
            </div>
          )
        })}
      </div>

      {/* Hidden locations — add chips */}
      {hidden.length > 0 && (
        <div className="border-t border-border pt-2 space-y-1.5">
          <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-ink-3">Add location</div>
          {hidden.map((loc) => (
            <button key={loc.id}
              onClick={() => {
                setRevealedLocationIds((prev) => { const next = new Set(prev); next.add(loc.id); return next })
                switchLocation(loc.id)
              }}
              className="w-full flex items-center gap-1.5 px-2 py-1 rounded border border-dashed border-border text-ink-3 text-[11px] hover:border-gold hover:text-gold-dark hover:bg-gold/5 transition-colors">
              <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
                <line x1="5" y1="1" x2="5" y2="9"/><line x1="1" y1="5" x2="9" y2="5"/>
              </svg>
              <span className="truncate">{loc.name}</span>
            </button>
          ))}
        </div>
      )}

      {all.length === 0 && (
        <p className="text-[11px] text-ink-3">No print locations configured. Set them up in Admin - Garments.</p>
      )}
    </div>
  )
}
